import React from "react";
import { Box, Paper, Typography, Grid, useTheme, alpha } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  MdEventNote,
  MdReceiptLong,
  MdPeople,
  MdPayments,
  MdAssignment,
} from "react-icons/md";

const QuickShortcutsBar = ({ userRole, hasPermission, isFeatureEnabled = () => true }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const isEmployeeRole = userRole?.toLowerCase() === "employee";

  const actions = [
    {
      title: "Apply Leave",
      icon: MdEventNote,
      color: "#ec4899",
      path: "/company/apply-leave",
      show: isFeatureEnabled("leave") && isEmployeeRole && (hasPermission("apply_leave.view") || hasPermission("apply_leave.create")),
    },
    {
      title: "Create Requisition",
      icon: MdReceiptLong,
      color: "#3b82f6",
      path: "/company/requisition-create",
      show: isFeatureEnabled("employees") && (hasPermission("my_requisitions.view") || hasPermission("requisitions.view") || true),
    },
    {
      title: "Staff Directory",
      icon: MdPeople,
      color: "#10b981",
      path: "/company/employee",
      show: isFeatureEnabled("employees") && (hasPermission("employee.view") || !isEmployeeRole),
    },
    {
      title: "Process Payroll",
      icon: MdPayments,
      color: "#8b5cf6",
      path: "/company/process-payroll",
      show: isFeatureEnabled("payroll") && (hasPermission("process_payroll.view") || !isEmployeeRole) && !isEmployeeRole,
    },
    {
      title: "Project Tasks",
      icon: MdAssignment,
      color: theme.palette.primary.main,
      path: "/company/tasks",
      show: isFeatureEnabled("projects") && (hasPermission("task.view") || hasPermission("projects.view")),
    },
  ];

  const visibleActions = actions.filter((a) => a.show);

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          mb: 2,
          letterSpacing: "-0.01em",
        }}
      >
        Quick Actions & Shortcuts
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {visibleActions.map((action, idx) => {
          const Icon = action.icon;
          return (
            <Paper
              key={action.title}
              component={motion.div}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              whileHover={{ y: -4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(action.path)}
              elevation={0}
              sx={{
                width: { xs: "calc(50% - 8px)", sm: "160px" },
                flex: { xs: "1 1 calc(50% - 8px)", sm: "0 0 160px" },
                p: 2.2,
                borderRadius: 1,
                cursor: "pointer",
                textAlign: "center",
                background: isDark ? "#111827" : "#ffffff",
                border: 1, borderColor: "divider",
                boxShadow: isDark
                  ? "0 4px 14px rgba(0, 0, 0, 0.2)"
                  : "0 4px 14px rgba(15, 23, 42, 0.04)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.2,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: action.color,
                  boxShadow: `0 6px 20px ${alpha(action.color, 0.25)}`,
                },
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "50%",
                  bgcolor: alpha(action.color, isDark ? 0.2 : 0.1),
                  color: action.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon size={24} />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  color: "text.primary",
                  lineHeight: 1.2,
                }}
              >
                {action.title}
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickShortcutsBar;
